/**
 * Player-time ↔ wall-clock, for one camera's unified playlist.
 *
 * The node serves a camera's range as ONE seekable VOD playlist: every finalized segment that
 * overlaps [rangeStart, rangeEnd), ascending, then the live tail (the still-recording slot's
 * closed fragments). Gaps in footage collapse — player-time is contiguous — so "what is on screen
 * at 12:10:27" needs this table.
 *
 * It MUST mirror the backend's generatePlaylist (local-node recordingService.js). Both
 * RecordingsPages carry the same logic inline; this is the pure copy the Live Wall's Recorded
 * mode uses, where six tiles each hold their own table.
 */
import type { RecordingSegment } from '../../api/contracts';

export interface OffsetRow {
  id: string | number;
  /** Seconds into the playlist. */
  playerStart: number;
  playerEnd: number;
  /** Epoch ms. */
  wallStart: number;
  wallEnd: number;
  live?: boolean;
}

/** In-progress segments older than this are not in the playlist's tail. Same as both pages. */
export const LIVE_TAIL_MAX_AGE_MS = 30 * 60 * 1000;

const t = (v: unknown) => new Date(v as string).getTime();

export function buildOffsetTable(
  segments: RecordingSegment[],
  rangeStartMs: number,
  rangeEndMs: number,
  now: number = Date.now(),
): OffsetRow[] {
  const rows: OffsetRow[] = [];
  let acc = 0;
  segments
    .filter((s) => (s.status || 'finalized') === 'finalized')
    .filter((s) => t(s.end_time) > rangeStartMs && t(s.start_time) < rangeEndMs)
    .sort((a, b) => t(a.start_time) - t(b.start_time))
    .forEach((s) => {
      // playlist_duration, not duration: the playlist gives each segment a whole number of
      // GOP-length chunks, a little under the media duration. Using `duration` drifts a chunk
      // per segment across a day.
      const dur = (s.playlist_duration ?? 0) > 0 ? s.playlist_duration!
        : ((s.duration ?? 0) > 0 ? s.duration! : 300);
      rows.push({ id: s.id, playerStart: acc, playerEnd: acc + dur, wallStart: t(s.start_time), wallEnd: t(s.end_time) });
      acc += dur;
    });

  // Live tail — one row per fresh in-progress segment, ending "now" (the playlist is fetched at
  // play time, so the real edge is within seconds of this).
  segments
    .filter((s) => s.status === 'in-progress')
    .filter((s) => t(s.start_time) < rangeEndMs && now - t(s.start_time) < LIVE_TAIL_MAX_AGE_MS)
    .sort((a, b) => t(a.start_time) - t(b.start_time))
    .forEach((s) => {
      const prevEnd = rows.length ? rows[rows.length - 1].wallEnd : rangeStartMs;
      const wallStart = Math.max(t(s.start_time), prevEnd, rangeStartMs);
      const wallEnd = Math.min(now, rangeEndMs);
      if (wallEnd - wallStart < 1500) return;
      const dur = (wallEnd - wallStart) / 1000;
      rows.push({ id: s.id, playerStart: acc, playerEnd: acc + dur, wallStart, wallEnd, live: true });
      acc += dur;
    });

  return rows;
}

/**
 * Where a row's PLAYABLE footage ends on the wall clock. The playlist gives a segment a whole
 * number of chunks, so its last few seconds are not in it: wall 12:00:00–12:05:00 may play only
 * 294 s. Treating those seconds as footage makes a following tile run into the NEXT segment's
 * first frames, read itself as minutes ahead, and seek back — at every boundary.
 */
export const playableEnd = (r: OffsetRow) => Math.min(r.wallEnd, r.wallStart + (r.playerEnd - r.playerStart) * 1000);

/** The row whose playable footage covers this instant, or null when the instant is in a gap. */
export function footageAt(rows: OffsetRow[], ms: number): OffsetRow | null {
  for (const r of rows) if (ms >= r.wallStart && ms < playableEnd(r)) return r;
  return null;
}

/** The first row that starts after this instant — where a camera in a gap resumes. */
export function nextFootageAfter(rows: OffsetRow[], ms: number): OffsetRow | null {
  for (const r of rows) if (r.wallStart > ms) return r;
  return null;
}

/**
 * Wall-clock → player-time. Inside footage it is exact; in a gap it snaps to the nearest
 * footage, which is what a timeline click means on the single-camera page. The wall uses
 * footageAt first and only calls this for instants that HAVE footage.
 */
export function wallToPlayer(rows: OffsetRow[], ms: number): number {
  if (!rows.length) return 0;
  for (const r of rows) {
    if (ms >= r.wallStart && ms <= r.wallEnd) {
      // A segment's wall span can exceed its playlist span (the tail chunk is dropped), so
      // clamp inside the row rather than bleeding into the next segment's player-time.
      return Math.min(r.playerStart + (ms - r.wallStart) / 1000, Math.max(r.playerStart, r.playerEnd - 0.05));
    }
  }
  let best = rows[0];
  let bestDist = Infinity;
  for (const r of rows) {
    const dist = ms < r.wallStart ? r.wallStart - ms : ms - r.wallEnd;
    if (dist < bestDist) { bestDist = dist; best = r; }
  }
  return ms > best.wallEnd ? Math.max(best.playerStart, best.playerEnd - 1) : best.playerStart;
}

/** Player-time → wall-clock ms, or null with an empty table. */
export function playerToWall(rows: OffsetRow[], pt: number): number | null {
  for (const r of rows) {
    if (pt >= r.playerStart && pt < r.playerEnd) return r.wallStart + (pt - r.playerStart) * 1000;
  }
  const last = rows[rows.length - 1];
  return last ? last.wallEnd : null;
}
